"use client"

import { Button, toast } from "@medusajs/ui"
import { useLocale, useTranslations } from "next-intl"
import { useRouter } from "next/navigation"
import { useMemo, useRef, useState } from "react"

import { Link, usePathname } from "@i18n/navigation"
import {
  claimTrial,
  startTrialBinding,
  type TrialState,
} from "@lib/data/trial"
import { formatAmount } from "@lib/util/money"
import { isPaypalApprovalUrl } from "@lib/util/next-path"
import { resolveRefusalCopy } from "@lib/util/refusal-copy"
import {
  canShowBindAndClaimButton,
  canShowClaimButton,
  deriveTrialPhase,
  resolveDiscordInviteUrl,
  trialGateBlock,
} from "@lib/util/trial-phase"
import { navigateToExternal } from "@lib/util/url"
import { CheckoutModal } from "@modules/checkout/components/checkout-modal"
import { MEMBERSHIP_REFRESH_KEY } from "./context"

type Props = {
  state: TrialState | null
  loading: boolean
  refresh: () => void
  onClose: () => void
}

/**
 * `error` 里其实混着两种字符串（见组件里的 `errorText` 注释）：本组件/钩子自己设的
 * **文案 key**，以及后端原句。key 必须翻译，否则会把 `trial_claim_failed` 这种
 * 内部标识直接印在弹窗里给顾客看。
 */
const TRIAL_ERROR_KEYS = new Set([
  "trial_claim_failed",
  "trial_bind_failed",
  "trial_bind_missing_id",
])

const dateLabel = (iso: string | null, locale: string): string | null => {
  if (!iso) return null
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return null
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(t)
}

/**
 * The shared trial modal (0.8.5 工单 08). Opens from the dashboard banner, the
 * floating marketing banner and the pricing card; its phase is derived from the
 * one eligibility payload:
 *
 *   anonymous                      → sign-in CTA (carries `next` back here)
 *   active unbound trial, not ended → bind phase ("+7 days with PayPal")
 *   eligible                       → claim phase
 *   otherwise                      → ineligible copy (already claimed / paid)
 *
 * Claiming is the server action `claimTrial`; the +7 button starts the PayPal
 * vault approval and navigates to its approve URL (the return page completes
 * it). The post-trial charge is stated explicitly (D4 / 偏离 4).
 */
export default function TrialClaimModal({
  state,
  loading,
  refresh,
  onClose,
}: Props) {
  const t = useTranslations("TrialClaim")
  const te = useTranslations("Errors")
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()

  const [busy, setBusy] = useState(false)
  // 2026-10-28（known-issues A10 同族）：`busy` 是 state，`setBusy(true)` 要等
  // React 提交才落到 DOM，同一个同步块里的第二次点击因此穿得过去 —— 一次意图
  // 会发出两个 `claimTrial()` server action。服务端有锁 + 唯一索引兜住，但白白
  // 多打一次。这两个 ref 是同步门闩，领取与绑定各一个。
  const claimInFlight = useRef(false)
  const bindInFlight = useRef(false)
  const [error, setError] = useState<string | null>(null)
  // Set after a successful claim so the modal can show the success copy even
  // before the refreshed state arrives.
  const [claimedEndsAt, setClaimedEndsAt] = useState<string | null>(null)
  // T06: the claim response carries the subscription id, so the bind button
  // works in the same tick even while the eligibility payload is stale.
  const [claimedSubscriptionId, setClaimedSubscriptionId] = useState<
    string | null
  >(null)
  const subscriptionId =
    state?.subscription?.subscriptionId ?? claimedSubscriptionId

  const price = state?.monthlyPrice
    ? formatAmount({
        amount: state.monthlyPrice.amount,
        currency_code: state.monthlyPrice.currencyCode,
        locale,
      })
    : null

  const trialEndsAt = useMemo(() => {
    if (claimedEndsAt) return claimedEndsAt
    return state?.subscription?.trialEndsAt ?? null
  }, [claimedEndsAt, state])

  const phase = useMemo(
    () => deriveTrialPhase(state, { claimedEndsAt, now: Date.now() }),
    [state, claimedEndsAt]
  )

  const showClaimButton = canShowClaimButton(phase)
  const showBindButton = canShowBindAndClaimButton(state, phase)
  // T13：门槛只拦「领取」（bind/已领取不受影响——他们已经在试用里了）。
  const gate = trialGateBlock(state)
  const gateBlocksClaim = gate !== null && phase === "claim"
  // T02: prefer the invite URL shipped with the eligibility payload (plugin
  // config); fall back to the build-time env var.
  const inviteUrl = resolveDiscordInviteUrl(state?.discord)

  const [linking, setLinking] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)

  /**
   * 2026-10-04（走查 item 7 / 工单 11 店面半）：门槛两个状态共用的「验证资格」
   * 动作。发起插件侧的 Discord 绑定（`reauth_required` 即重授权），并留下一次性
   * 标记让回跳后的资格刷新带 `?refresh=1`。
   *
   * D2 fix (0.9.3 ticket 17)：绑定是浏览器直调（只有浏览器带着会话 cookie）。
   * 2026-10-30（阶段 6 · 身份搬家）：接口改成新站自己的 `/api/auth/link-social`
   * —— 同源、不需要 publishable key。
   */
  const verifyDiscord = () => {
    setLinking(true)
    setLinkError(null)
    void (async () => {
      try {
        const res = await fetch("/api/auth/link-social", {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            provider: "discord",
            callbackURL: `${window.location.origin}/account`,
          }),
        })
        const data = (await res.json().catch(() => null)) as
          | { url?: string }
          | null
        if (res.ok && typeof data?.url === "string") {
          try {
            window.sessionStorage.setItem(MEMBERSHIP_REFRESH_KEY, "1")
          } catch {
            // 私密模式/禁用存储：复核退化为普通（缓存内）读取，不阻断绑定。
          }
          if (navigateToExternal(data.url)) return
        }
      } catch {
        // network failure falls through to the error copy below
      }
      setLinking(false)
      setLinkError(t("discord.verifyFailed"))
    })()
  }

  const onClaim = async () => {
    if (claimInFlight.current) return
    claimInFlight.current = true
    try {
      await runClaim()
    } finally {
      claimInFlight.current = false
    }
  }

  const runClaim = async () => {
    setBusy(true)
    setError(null)
    const result = await claimTrial().catch(() => ({
      status: "error" as const,
      message: "trial_claim_failed",
    }))
    setBusy(false)
    if (result.status === "ok") {
      setClaimedEndsAt(result.trialEndsAt)
      setClaimedSubscriptionId(result.subscriptionId)
      // 2026-10-07（通知规范 Task 11）：claim 成功原来只改弹窗内文本
      // （`trial-success`）—— 弹窗内展示保留（它是持久状态），但一次性结果
      // 要发 toast：用户可能已经滚到别处，或干脆没盯着弹窗看。
      toast.success(t("claim.done"))
      refresh()
      router.refresh()
      return
    }
    setError(result.message)
  }

  /**
   * The right-hand button (T06). Binds when the trial is already claimed;
   * otherwise it claims first and binds with the id the claim returned, so the
   * buyer never has to reopen the modal to earn the +7 days.
   */
  const onBind = async () => {
    if (bindInFlight.current) return
    bindInFlight.current = true
    try {
      await runBind()
    } finally {
      bindInFlight.current = false
    }
  }

  const runBind = async () => {
    setBusy(true)
    setError(null)

    let subId = subscriptionId
    if (!subId) {
      const claimed = await claimTrial().catch(() => ({
        status: "error" as const,
        message: "trial_claim_failed",
      }))
      if (claimed.status !== "ok") {
        setBusy(false)
        setError(claimed.message)
        return
      }
      setClaimedEndsAt(claimed.trialEndsAt)
      setClaimedSubscriptionId(claimed.subscriptionId)
      toast.success(t("claim.done"))
      refresh()
      router.refresh()
      subId = claimed.subscriptionId
      if (!subId) {
        setBusy(false)
        setError("trial_bind_failed")
        return
      }
    }

    const result = await startTrialBinding(subId).catch(() => ({
      status: "error" as const,
      message: "trial_bind_failed",
    }))
    if (result.status === "ok") {
      if (result.phase === "bound") {
        // The engine adopted a method the customer already had on file, so
        // there is no approval to visit and the trial is bound already. The
        // modal's phase comes from server state, so re-read it.
        setBusy(false)
        setError(null)
        toast.success(t("bind.done"))
        refresh()
        router.refresh()
        return
      }

      // Server-provided PayPal approve URL — validate the https/PayPal shape
      // before navigating (never a raw open redirect).
      if (!isPaypalApprovalUrl(result.approveUrl)) {
        setBusy(false)
        setError("trial_bind_failed")
        return
      }
      window.location.assign(result.approveUrl)
      return
    }
    setBusy(false)
    setError(result.message)
  }

  /**
   * 弹窗底部那句错误。
   *
   * `error` 里混着两种东西：本组件/钩子自己设的文案 key（`trial_claim_failed`
   * 之类），以及后端原句（`lib/data/trial.ts` 的 `messageOf(error)` 取的是
   * `error.message`，原样透传）。所以先按 key 查，再试拒绝文案表，最后才原句。
   *
   * 为什么需要中间那一层：`@mengyyy369/reorder` 把试用拒绝的英文句子登记成
   * `TRIAL_CLAIM_CUSTOMER_REFUSALS` 的 **可原文外泄**项（A3），重试一次已经领过的
   * 试用，顾客会在中文界面读到整句英文。拒绝文案表是仓库里现成的翻译层
   * （`lib/util/refusal-copy.ts`，checkout 也在用），这里只是接上它。
   */
  const errorText = (raw: string): string => {
    if (TRIAL_ERROR_KEYS.has(raw)) {
      return raw === "trial_claim_failed"
        ? t("claim.failed")
        : t("bind.failed")
    }
    const refusal = resolveRefusalCopy({ message: raw })
    if (refusal) return te(`refusal.${refusal.key}`, refusal.values)
    return raw
  }

  const renderBody = () => {
    if (loading && !state) {
      return <p className="txt-medium text-ui-fg-subtle">{t("loading")}</p>
    }

    if (phase === "signIn") {
      return (
        <div className="flex flex-col gap-y-4">
          <p className="txt-medium text-ui-fg-base">{t("signIn.body")}</p>
          <Button asChild className="w-full">
            <Link href={{ pathname: "/account", query: { next: pathname } }}>
              {t("signIn.cta")}
            </Link>
          </Button>
        </div>
      )
    }

    if (phase === "ineligible") {
      return (
        <p className="txt-medium text-ui-fg-subtle" data-testid="trial-ineligible">
          {t("ineligible")}
        </p>
      )
    }

    const ends = dateLabel(trialEndsAt, locale)

    return (
      <div className="flex flex-col gap-y-4">
        {phase === "claimed" || phase === "bind" ? (
          <p className="txt-medium text-ui-fg-base" data-testid="trial-success">
            {ends ? t("claimed.until", { date: ends }) : t("claimed.title")}
          </p>
        ) : (
          <p className="txt-medium text-ui-fg-subtle">{t("claim.body")}</p>
        )}

        {/* The charge only exists once a card is bound. The claim phase is a
            plain 7-day trial; claimed/bind are the two states whose +7-day CTA
            creates the subscription, so their copy is conditional on that
            action ("binding a payment method …"), never a flat charge claim. */}
        <p className="txt-small text-ui-fg-subtle" data-testid="trial-terms">
          {phase === "claimed" || phase === "bind"
            ? price
              ? t("terms.renew.withPrice", { price })
              : t("terms.renew.plain")
            : t("terms.claim")}
        </p>

        {/* T06 (2026-10-02 walkthrough): the two actions sit side by side at
            the bottom — white claim on the left, PayPal bind on the right.
            The right one chains a claim when nothing is claimed yet, so it
            only renders while it can actually do something (no dead control). */}
        {gateBlocksClaim ? (
          <div className="flex flex-col gap-y-3" data-testid="trial-discord-gate">
            <p className="txt-medium text-ui-fg-base">
              {gate === "discord_reauth" ? t("discord.reauth") : t("discord.join")}
            </p>
            {gate === "discord_required" && inviteUrl ? (
              <a
                href={inviteUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-10 w-full items-center justify-center rounded-rounded bg-[#5865F2] px-6 text-small-regular text-white transition-colors hover:bg-[#4752c4]"
                data-testid="trial-discord-cta"
              >
                {t("discord.cta")}
              </a>
            ) : null}

            {/* 2026-10-04（走查 item 7）：两个门槛态都必须有一个可点的动作——
                `discord_reauth` 此前没有任何 CTA。 */}
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              isLoading={linking}
              disabled={linking}
              onClick={verifyDiscord}
              data-testid="trial-discord-verify"
            >
              {t("discord.verify")}
            </Button>
            {linkError ? (
              <p
                className="txt-small text-rose-500"
                data-testid="trial-discord-verify-error"
              >
                {linkError}
              </p>
            ) : null}
          </div>
        ) : null}

        {!gateBlocksClaim && (showClaimButton || showBindButton) ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            {showClaimButton ? (
              <Button
                variant="secondary"
                className="flex-1 bg-white text-black hover:bg-white/90"
                isLoading={busy}
                disabled={busy}
                onClick={onClaim}
                data-testid="trial-claim-button"
              >
                {t("claim.cta")}
              </Button>
            ) : null}

            {showBindButton ? (
              <Button
                className="flex-1 border-0 text-white"
                style={{
                  background: "linear-gradient(to right, #002E5D, #2774ae)",
                }}
                isLoading={busy}
                disabled={busy}
                onClick={onBind}
                data-testid="trial-bind-button"
              >
                {t("bind.cta", { days: state?.offer?.bonusDays ?? 7 })}
              </Button>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p className="txt-small text-rose-500" data-testid="trial-error">
            {errorText(error)}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <CheckoutModal open onClose={onClose} title={t("title")}>
      {renderBody()}
    </CheckoutModal>
  )
}
